import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { createCategory, updateCategory } from '../../../shared/api/categories.js'
import type { Category } from '../../../shared/types/models.js'
import {
  normalizeSortOrder,
  validateNameField,
  validateSortOrderField,
} from '../../../shared/utils/catalog.js'
import { toFieldErrors, type FieldErrors } from '../../../shared/utils/formErrors.js'

interface Props {
  organizationId: string
  menuId: string
  initial?: Category | null
  disabled: boolean
  onSaved: (category: Category) => void
  onCancel: () => void
}

export default function CategoryForm({ organizationId, menuId, initial, disabled, onSaved, onCancel }: Props) {
  const { t } = useTranslation()
  const isEdit = initial !== undefined && initial !== null

  const [name, setName] = useState(initial?.name ?? '')
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [sortOrder, setSortOrder] = useState(initial !== undefined && initial !== null ? String(initial.sortOrder) : '0')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saving, setSaving] = useState(false)

  function handleChange() {
    if (errors.name || errors.slug || errors.sortOrder || errors.general) setErrors({})
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (disabled) return

    const fieldErrors: FieldErrors = {}
    const nameError = validateNameField(name)
    if (nameError === 'required') fieldErrors.name = t('error.fieldRequired')
    const sortError = validateSortOrderField(sortOrder)
    if (sortError) fieldErrors.sortOrder = t('menu.sortOrderInvalid')
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    const payload = {
      name: name.trim(),
      slug: slug.trim() || undefined,
      description: description.trim() || undefined,
      sortOrder: normalizeSortOrder(sortOrder) ?? 0,
    }

    setSaving(true)
    setErrors({})
    try {
      const category = isEdit
        ? await updateCategory(organizationId, menuId, initial.id, {
            name: payload.name,
            description: payload.description,
            sortOrder: payload.sortOrder,
          })
        : await createCategory(organizationId, menuId, payload)
      onSaved(category)
    } catch (err: unknown) {
      setErrors(toFieldErrors(err, ['name', 'slug', 'sortOrder'], t('error.serverError')))
      setSaving(false)
    }
  }

  return (
    <div className="settings-card create-card">
      <h3>{isEdit ? t('menu.editCategory') : t('menu.createCategory')}</h3>

      {errors.general && <div className="form-error-banner">{errors.general}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`categoryName-${isEdit ? initial.id : 'new'}`}>{t('menu.name')} *</label>
            <input
              id={`categoryName-${isEdit ? initial.id : 'new'}`}
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value)
                handleChange()
              }}
              maxLength={200}
              required
              disabled={saving || disabled}
              autoComplete="off"
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`categorySortOrder-${isEdit ? initial.id : 'new'}`}>{t('menu.sortOrder')}</label>
            <input
              id={`categorySortOrder-${isEdit ? initial.id : 'new'}`}
              type="number"
              min={0}
              step={1}
              value={sortOrder}
              onChange={e => {
                setSortOrder(e.target.value)
                handleChange()
              }}
              disabled={saving || disabled}
            />
            {errors.sortOrder && <span className="field-error">{errors.sortOrder}</span>}
          </div>
        </div>

        <div className="form-field">
          <label htmlFor={`categorySlug-${isEdit ? initial.id : 'new'}`}>{t('menu.slug')}</label>
          <input
            id={`categorySlug-${isEdit ? initial.id : 'new'}`}
            type="text"
            value={slug}
            onChange={e => {
              setSlug(e.target.value)
              handleChange()
            }}
            maxLength={100}
            disabled={saving || disabled || isEdit}
            autoComplete="off"
          />
          {isEdit ? (
            <span className="field-hint">{t('menu.slugReadonlyHint')}</span>
          ) : (
            <span className="field-hint">{t('menu.slugHint')}</span>
          )}
          {errors.slug && <span className="field-error">{errors.slug}</span>}
        </div>

        <div className="form-field">
          <label htmlFor={`categoryDescription-${isEdit ? initial.id : 'new'}`}>{t('menu.description')}</label>
          <textarea
            id={`categoryDescription-${isEdit ? initial.id : 'new'}`}
            value={description}
            onChange={e => {
              setDescription(e.target.value)
              handleChange()
            }}
            rows={3}
            maxLength={2000}
            disabled={saving || disabled}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || disabled}>
            {saving ? t('menu.saving') : isEdit ? t('menu.save') : t('menu.createCategory')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
            {t('menu.cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}